// src/TimelineBackup.js - Simplified Backup System (Database + SRV only)
import React, { useState, useEffect, useCallback } from 'react';
import './TimelineBackup.css';
import { getRuntimeConfig } from '../config';

const TimelineBackup = () => {
  const [backupStatuses, setBackupStatuses] = useState({
    database: { inProgress: false, lastBackup: null, size: null, error: null, count: 0 },
    srv: { inProgress: false, lastBackup: null, size: null, error: null, count: 0 }
  });

  // Traditional backup lists for other types
  const [backupLists, setBackupLists] = useState({
    database: [],
    srv: []
  });
  
  // Selected backup for restore
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  
  // Modal states
  const [showBrowseModal, setShowBrowseModal] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(null);

  const token = localStorage.getItem('token');
  const apiUrlBase = `${getRuntimeConfig().API_BASE_URL}/api`;

  // Fetch other backup lists (database, srv)
  const fetchOtherBackupLists = useCallback(async () => {
    const backupTypes = [
      { type: 'database', endpoint: `${apiUrlBase}/admin/backup/list` },
      { type: 'srv', endpoint: `${apiUrlBase}/backup/srv/list` }
    ];
    
    for (const { type, endpoint } of backupTypes) {
      try {
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const backupList = data.backups || [];
          
          setBackupLists(prev => ({
            ...prev,
            [type]: backupList
          }));
          
          if (backupList.length > 0) {
            const latest = backupList[0];
            const formattedDateTime = `${latest.date} ${latest.time}`;
            setBackupStatuses(prev => ({
              ...prev,
              [type]: {
                ...prev[type],
                lastBackup: formattedDateTime,
                size: latest.size,
                count: backupList.length
              }
            }));
          }
        }
      } catch (error) {
        console.error(`Error fetching ${type} backups:`, error);
      }
    }
  }, [token, apiUrlBase]);

  // Initial load
  useEffect(() => {
    fetchOtherBackupLists();
    
    const interval = setInterval(() => {
      fetchOtherBackupLists();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchOtherBackupLists]);

  // Manual backup handlers
  const handleManualBackup = async (type) => {
    setBackupStatuses(prev => ({
      ...prev,
      [type]: { ...prev[type], inProgress: true, error: null }
    }));

    try {
      let endpoint;
      switch (type) {
        case 'database':
          endpoint = `${apiUrlBase}/admin/backup/manual`;
          break;
        case 'srv':
          endpoint = `${apiUrlBase}/backup/srv`;
          break;
        default:
          throw new Error('Unknown backup type');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || `Backup failed with status ${response.status}`);
      }
      
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} backup started successfully!`);
      
      // Reset the inProgress status after a short delay
      setTimeout(() => {
        setBackupStatuses(prev => ({
          ...prev,
          [type]: { ...prev[type], inProgress: false }
        }));
        // Fetch the new data after a longer delay
        setTimeout(() => {
          fetchOtherBackupLists();
        }, 5000);
      }, 2000);

    } catch (error) {
      console.error(`${type} backup error:`, error);
      setBackupStatuses(prev => ({
        ...prev,
        [type]: { ...prev[type], inProgress: false, error: error.message }
      }));
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} backup failed: ${error.message}`);
    }
  };

  // Handle traditional backup browsing
  const handleBrowseTraditional = (type) => {
    setSelectedBackup(null);
    setRestoreFile(null);
    setShowBrowseModal(type);
  };

  // Handle restore from existing backup file
  const handleRestoreFromBackup = (type, backup) => {
    setSelectedBackup(backup);
    setRestoreFile(null);
    setShowRestoreModal(type);
  };

  // Handle restore from uploaded file
  const handleRestoreFromUpload = (type) => {
    setSelectedBackup(null);
    setRestoreFile(null);
    setShowRestoreModal(type);
  };

  // Confirm restore for traditional backups
  const confirmTraditionalRestore = async () => {
    if (!showRestoreModal) return;

    try {
      let endpoint;
      let requestOptions = {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      };

      const isFileUpload = restoreFile && !selectedBackup;
      const isExistingBackup = selectedBackup && !restoreFile;

      if (!isFileUpload && !isExistingBackup) {
        alert('Please either select a backup or upload a file');
        return;
      }

      switch (showRestoreModal) {
        case 'database':
          if (isFileUpload) {
            alert('Database restore from upload not supported. Please use existing backups.');
            return;
          } else if (isExistingBackup) {
            endpoint = `${apiUrlBase}/admin/restore`;
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.body = JSON.stringify({ filename: selectedBackup.filename });
          }
          break;
          
        case 'srv':
          if (isFileUpload) {
            endpoint = `${apiUrlBase}/backup/srv/restore/upload`;
            const formData = new FormData();
            formData.append('srv_backup_file', restoreFile);
            requestOptions.body = formData;
          } else if (isExistingBackup) {
            endpoint = `${apiUrlBase}/backup/srv/restore`;
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.body = JSON.stringify({ filename: selectedBackup.filename });
          }
          break;
          
        default:
          throw new Error('Unknown restore type');
      }

      const response = await fetch(endpoint, requestOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const result = await response.json();
      alert(`${showRestoreModal.charAt(0).toUpperCase() + showRestoreModal.slice(1)} restore completed successfully!`);
      
      setShowRestoreModal(null);
      setSelectedBackup(null);
      setRestoreFile(null);
      
      if (showRestoreModal === 'srv') {
        setTimeout(() => window.location.reload(), 2000);
      } else {
        fetchOtherBackupLists();
      }

    } catch (error) {
      console.error('Restore error:', error);
      alert(`Restore failed: ${error.message}`);
    }
  };

  // Backup Card Component
  const BackupCard = ({ type, title, icon }) => {
    const status = backupStatuses[type];

    return (
      <div className="backup-card">
        <div className="backup-card-header">
          <div className="backup-icon">{icon}</div>
          <div className="backup-title">{title}</div>
        </div>
        
        <div className="backup-info">
          <div className="backup-stat">
            <span className="stat-label">Latest:</span>
            <span className="stat-value">{status.lastBackup || 'Never'}</span>
          </div>
          <div className="backup-stat">
            <span className="stat-label">Size:</span>
            <span className="stat-value">{status.size || 'N/A'}</span>
          </div>
          <div className="backup-stat">
            <span className="stat-label">Count:</span>
            <span className="stat-value">{status.count || 0} backups</span>
          </div>
        </div>

        {status.error && (
          <div className="backup-error">
            Error: {status.error}
          </div>
        )}

        <div className="backup-actions">
          <button
            className="backup-btn manual"
            onClick={() => handleManualBackup(type)}
            disabled={status.inProgress}
            type="button"
          >
            {status.inProgress ? 'Creating...' : 'Backup'}
          </button>
          
          <button
            className="backup-btn browse"
            onClick={() => handleBrowseTraditional(type)}
            disabled={(backupLists[type] || []).length === 0}
            type="button"
          >
            Browse ({(backupLists[type] || []).length})
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="timeline-backup-container">
      <div className="backup-grid">
        <BackupCard type="database" title="Database" icon="🗄️" />
        <BackupCard type="srv" title="Server Files" icon="📦" />
      </div>

      {/* Browse Modal */}
      {showBrowseModal && (
        <div className="modal-overlay" onClick={() => setShowBrowseModal(null)}>
          <div className="modal-content browse-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Browse {showBrowseModal.charAt(0).toUpperCase() + showBrowseModal.slice(1)} Backups</h3>
              <button className="modal-close" onClick={() => setShowBrowseModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="backup-list">
                {backupLists[showBrowseModal]?.map((backup, index) => (
                  <div key={index} className="backup-item">
                    <div className="backup-item-info">
                      <div className="backup-item-name">{backup.filename}</div>
                      <div className="backup-item-meta">
                        {backup.date} {backup.time} - {backup.size}
                      </div>
                    </div>
                    <div className="backup-item-actions">
                      <button
                        className="restore-btn"
                        onClick={() => {
                          setShowBrowseModal(null);
                          handleRestoreFromBackup(showBrowseModal, backup);
                        }}
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
                {backupLists[showBrowseModal]?.length === 0 && (
                  <div className="no-backups">No backups available</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="upload-restore-btn"
                  onClick={() => {
                    setShowBrowseModal(null);
                    handleRestoreFromUpload(showBrowseModal);
                  }}
                >
                  Upload & Restore
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traditional Backup Restore Modal */}
      {showRestoreModal && (
        <div className="modal-overlay" onClick={() => setShowRestoreModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Restore {showRestoreModal.charAt(0).toUpperCase() + showRestoreModal.slice(1)}</h3>
              <button className="modal-close" onClick={() => setShowRestoreModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {selectedBackup ? (
                <div className="restore-confirmation">
                  <p>Are you sure you want to restore this backup?</p>
                  <div className="backup-details">
                    <strong>{selectedBackup.filename}</strong><br/>
                    {selectedBackup.date} {selectedBackup.time} - {selectedBackup.size}
                  </div>
                  <div className="modal-actions">
                    <button className="cancel-btn" onClick={() => setShowRestoreModal(null)}>
                      Cancel
                    </button>
                    <button className="confirm-btn" onClick={confirmTraditionalRestore}>
                      Confirm Restore
                    </button>
                  </div>
                </div>
              ) : (
                <div className="restore-upload">
                  <p>Upload a backup file to restore:</p>
                  <input
                    type="file"
                    accept={showRestoreModal === 'database' ? '.sql' : '.tar.gz,.zip'}
                    onChange={(e) => setRestoreFile(e.target.files[0])}
                  />
                  <div className="modal-actions">
                    <button className="cancel-btn" onClick={() => setShowRestoreModal(null)}>
                      Cancel
                    </button>
                    <button
                      className="confirm-btn"
                      onClick={confirmTraditionalRestore}
                      disabled={!restoreFile}
                    >
                      Upload & Restore
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineBackup;